import React, { useState } from 'react';
import { Control, Path, useForm } from 'react-hook-form';
import { LuEye, LuFileText, LuPencil, LuPlus, LuTrash2 } from 'react-icons/lu';

import type { NoteTemplate, NoteTemplateRequest } from '@ndvr/customer-api';

import { MinimalTiptapEditor, MinimalTiptapEditorNoToolbar } from '@/components/minimal-tiptap';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { convertPlainTextToHtml, formatDateTime, titleCase } from 'utils';
import { useAuthContext } from 'views/auth/Auth';

import { WorkspaceSettingBanner } from '../WorkspaceSettingBanner';
import {
    NOTE_TEMPLATE_TYPES,
    useCreateNoteTemplateMutation,
    useDeleteNoteTemplateMutation,
    useNoteTemplatesQuery,
    useUpdateNoteTemplateMutation,
} from './note-template-queries';

type CreateTemplateFormValues = {
    subject: string;
    content: string;
    type: NoteTemplate['type'] | null;
};

type EditTemplateFormValues = {
    subject: string;
    content: string;
};

const emptyCreateValues: CreateTemplateFormValues = {
    subject: '',
    content: '',
    type: null,
};

const emptyEditValues: EditTemplateFormValues = {
    subject: '',
    content: '',
};

type ContentFieldValues = { content: string };

const TemplateContentField = <T extends ContentFieldValues>({
    control,
    editorKey,
    disabled,
}: {
    control: Control<T>;
    editorKey: string;
    disabled: boolean;
}) => (
    <FormField
        control={control}
        name={'content' as Path<T>}
        rules={{ required: 'Content is required' }}
        render={({ field }) => (
            <FormItem>
                <FormControl>
                    <MinimalTiptapEditor
                        key={editorKey}
                        value={field.value}
                        output="html"
                        onChange={(value) => {
                            if (typeof value === 'string') {
                                field.onChange(value);
                            }
                        }}
                        className="mt-2"
                        editorContentClassName="p-5"
                        placeholder="Start typing template content..."
                        editable={!disabled}
                    />
                </FormControl>
                <FormMessage />
            </FormItem>
        )}
    />
);

export const NoteTemplatesAdmin = () => {
    const { availableTenants, currentTenant, setTenant } = useAuthContext();
    const { data: noteTemplates = [], isLoading, isError, refetch } = useNoteTemplatesQuery();
    const deleteTemplateMutation = useDeleteNoteTemplateMutation();
    const createTemplateMutation = useCreateNoteTemplateMutation();
    const updateTemplateMutation = useUpdateNoteTemplateMutation();

    const [preview, setPreview] = useState<NoteTemplate | null>(null);
    const [editingTemplate, setEditingTemplate] = useState<NoteTemplate | null>(null);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [deleteConfirmTemplateId, setDeleteConfirmTemplateId] = useState<string | null>(null);

    const createForm = useForm<CreateTemplateFormValues>({
        mode: 'onChange',
        defaultValues: emptyCreateValues,
    });

    const editForm = useForm<EditTemplateFormValues>({
        mode: 'onChange',
        defaultValues: emptyEditValues,
    });

    const isMutatingTemplate = [createTemplateMutation, updateTemplateMutation, deleteTemplateMutation].some(
        ({ isPending }) => isPending,
    );

    const handleTenantChange = (tenantGUID: string) => {
        setPreview(null);
        setEditingTemplate(null);
        setIsCreateDialogOpen(false);
        setDeleteConfirmTemplateId(null);
        createForm.reset(emptyCreateValues);
        editForm.reset(emptyEditValues);
        setTenant(tenantGUID);
    };

    const handleCloseCreateDialog = () => {
        setIsCreateDialogOpen(false);
        createForm.reset(emptyCreateValues);
    };

    const handleEditTemplate = (template: NoteTemplate) => {
        setPreview(null);
        setEditingTemplate(template);
        editForm.reset({
            subject: template.subject,
            content: convertPlainTextToHtml(template.content ?? ''),
        });
    };

    const handleCloseEditDialog = () => {
        setEditingTemplate(null);
        editForm.reset(emptyEditValues);
    };

    const handleSaveTemplate = editForm.handleSubmit((values) => {
        if (!editingTemplate) {
            return;
        }

        const payload: NoteTemplateRequest = {
            type: editingTemplate.type,
            subject: values.subject,
            content: values.content,
        };

        updateTemplateMutation.mutate(
            {
                noteTemplateGUID: editingTemplate.noteTemplateGUID,
                template: payload,
            },
            {
                onSuccess: handleCloseEditDialog,
            },
        );
    });

    const handleCreateTemplate = createForm.handleSubmit((values) => {
        const type = values.type;
        if (!type) {
            createForm.setError('type', { type: 'required', message: 'Type is required' });
            return;
        }

        createForm.clearErrors('type');

        const payload: NoteTemplateRequest = {
            type,
            subject: values.subject,
            content: values.content,
        };

        createTemplateMutation.mutate(payload, {
            onSuccess: handleCloseCreateDialog,
        });
    });

    const handleConfirmDelete = () => {
        if (!deleteConfirmTemplateId) {
            return;
        }
        deleteTemplateMutation.mutate(deleteConfirmTemplateId, {
            onSettled: () => setDeleteConfirmTemplateId(null),
        });
    };

    return (
        <>
            <WorkspaceSettingBanner
                resourceLabel="Note templates"
                availableTenants={availableTenants}
                currentTenant={currentTenant}
                disabled={isMutatingTemplate}
                onTenantChange={handleTenantChange}
            />
            <Card>
                <CardHeader className="py-4">
                    <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2 text-base font-medium">
                            <LuFileText className="h-4 w-4" />
                            Note Templates
                        </CardTitle>
                        <Button size="sm" onClick={() => setIsCreateDialogOpen(true)}>
                            <LuPlus className="mr-1 h-4 w-4" />
                            Create Template
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {isLoading ? (
                        <div className="text-muted-foreground py-6 text-center">Loading templates…</div>
                    ) : isError ? (
                        <div className="text-destructive py-6 text-center text-sm">
                            Failed to load templates.
                            <Button variant="link" onClick={() => refetch()} className="ml-2 p-0">
                                Retry
                            </Button>
                        </div>
                    ) : noteTemplates.length > 0 ? (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Title</TableHead>
                                    <TableHead>Type</TableHead>
                                    <TableHead>Last Updated</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {noteTemplates.map((template) => (
                                    <TableRow key={template.noteTemplateGUID}>
                                        <TableCell
                                            className="cursor-pointer font-medium"
                                            onClick={() => setPreview(template)}
                                        >
                                            {template.subject}
                                        </TableCell>
                                        <TableCell
                                            className="text-muted-foreground cursor-pointer"
                                            onClick={() => setPreview(template)}
                                        >
                                            {titleCase(template.type)}
                                        </TableCell>
                                        <TableCell className="cursor-pointer" onClick={() => setPreview(template)}>
                                            {formatDateTime(template.updatedAt)}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => setPreview(template)}
                                                    title="Preview"
                                                >
                                                    <LuEye size={16} />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleEditTemplate(template)}
                                                    title="Edit"
                                                >
                                                    <LuPencil size={16} />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() =>
                                                        setDeleteConfirmTemplateId(template.noteTemplateGUID)
                                                    }
                                                    title="Delete"
                                                    disabled={deleteTemplateMutation.isPending}
                                                >
                                                    <LuTrash2 size={16} />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <div className="text-muted-foreground py-6 text-center">No templates found.</div>
                    )}
                </CardContent>
            </Card>
            <Dialog open={!!preview} onOpenChange={(open) => !open && setPreview(null)}>
                <DialogContent className="flex max-h-[90vh] max-w-3xl flex-col sm:max-w-4xl">
                    <DialogHeader>
                        <DialogTitle>{preview?.subject}</DialogTitle>
                        <DialogDescription>
                            {preview
                                ? `Last updated ${formatDateTime(preview.updatedAt)} • ${titleCase(preview.type)}`
                                : 'Preview template'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="-mx-6 min-h-0 flex-1 overflow-y-auto px-6">
                        <MinimalTiptapEditorNoToolbar
                            key={preview?.noteTemplateGUID ?? 'note-template-preview'}
                            value={convertPlainTextToHtml(preview?.content ?? '')}
                            className="mt-2"
                            editorContentClassName="p-5"
                            output="html"
                            editable={false}
                            editorClassName="focus:outline-hidden"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPreview(null)}>
                            Close
                        </Button>
                        {preview && <Button onClick={() => handleEditTemplate(preview)}>Edit Template</Button>}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!editingTemplate} onOpenChange={(open) => !open && handleCloseEditDialog()}>
                <DialogContent className="flex max-h-[90vh] max-w-3xl flex-col sm:max-w-4xl">
                    <DialogHeader>
                        <DialogTitle>Edit Template</DialogTitle>
                        <DialogDescription>Update the template subject and content.</DialogDescription>
                    </DialogHeader>
                    <Form {...editForm}>
                        <form onSubmit={handleSaveTemplate} className="flex min-h-0 flex-1 flex-col gap-4">
                            <div className="-mx-6 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6">
                                <FormField
                                    control={editForm.control}
                                    name="subject"
                                    rules={{ required: 'Subject is required' }}
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Subject</FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    placeholder="Template subject"
                                                    disabled={updateTemplateMutation.isPending}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                {editingTemplate && (
                                    <div className="text-muted-foreground text-sm">
                                        Template type: {titleCase(editingTemplate.type)}
                                    </div>
                                )}
                                <TemplateContentField
                                    control={editForm.control}
                                    editorKey={editingTemplate?.noteTemplateGUID ?? 'note-template-edit'}
                                    disabled={updateTemplateMutation.isPending}
                                />
                            </div>
                            <DialogFooter>
                                <Button
                                    variant="outline"
                                    type="button"
                                    onClick={handleCloseEditDialog}
                                    disabled={updateTemplateMutation.isPending}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={
                                        updateTemplateMutation.isPending ||
                                        !editingTemplate ||
                                        !editForm.watch('subject')?.trim() ||
                                        !editForm.watch('content')?.trim()
                                    }
                                >
                                    {updateTemplateMutation.isPending ? 'Saving…' : 'Save Changes'}
                                </Button>
                            </DialogFooter>
                        </form>
                    </Form>
                </DialogContent>
            </Dialog>

            <Dialog open={isCreateDialogOpen} onOpenChange={(open) => !open && handleCloseCreateDialog()}>
                <DialogContent className="flex max-h-[90vh] max-w-3xl flex-col sm:max-w-4xl">
                    <DialogHeader>
                        <DialogTitle>Create Template</DialogTitle>
                        <DialogDescription>
                            Define a reusable template for advisors to apply to meeting notes.
                        </DialogDescription>
                    </DialogHeader>
                    <Form {...createForm}>
                        <form onSubmit={handleCreateTemplate} className="flex min-h-0 flex-1 flex-col gap-4">
                            <div className="-mx-6 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6">
                                <FormField
                                    control={createForm.control}
                                    name="subject"
                                    rules={{ required: 'Subject is required' }}
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Subject</FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    placeholder="Template subject"
                                                    disabled={createTemplateMutation.isPending}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={createForm.control}
                                    name="type"
                                    rules={{ required: 'Type is required' }}
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Type</FormLabel>
                                            <FormControl>
                                                <select
                                                    ref={field.ref}
                                                    name={field.name}
                                                    value={field.value ?? ''}
                                                    onChange={(event) =>
                                                        field.onChange(
                                                            event.target.value
                                                                ? (event.target.value as NoteTemplate['type'])
                                                                : null,
                                                        )
                                                    }
                                                    onBlur={field.onBlur}
                                                    className="border-input focus-visible:ring-ring focus-visible:ring-offset-background bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm ring-offset-2 transition-colors focus-visible:ring-2 focus-visible:outline-none"
                                                    disabled={createTemplateMutation.isPending}
                                                >
                                                    <option value="" disabled>
                                                        Select a type
                                                    </option>
                                                    {NOTE_TEMPLATE_TYPES.map((templateType) => (
                                                        <option key={templateType} value={templateType}>
                                                            {titleCase(templateType)}
                                                        </option>
                                                    ))}
                                                </select>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <TemplateContentField
                                    control={createForm.control}
                                    editorKey={
                                        isCreateDialogOpen ? 'note-template-create' : 'note-template-create-idle'
                                    }
                                    disabled={createTemplateMutation.isPending}
                                />
                            </div>
                            <DialogFooter>
                                <Button
                                    variant="outline"
                                    type="button"
                                    onClick={handleCloseCreateDialog}
                                    disabled={createTemplateMutation.isPending}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={
                                        createTemplateMutation.isPending ||
                                        !createForm.watch('subject')?.trim() ||
                                        !createForm.watch('type') ||
                                        !createForm.watch('content')?.trim()
                                    }
                                >
                                    {createTemplateMutation.isPending ? 'Creating…' : 'Create Template'}
                                </Button>
                            </DialogFooter>
                        </form>
                    </Form>
                </DialogContent>
            </Dialog>

            <Dialog open={!!deleteConfirmTemplateId} onOpenChange={(open) => !open && setDeleteConfirmTemplateId(null)}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Delete template</DialogTitle>
                        <DialogDescription>
                            This will remove the template for all advisors. This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDeleteConfirmTemplateId(null)}
                            disabled={deleteTemplateMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleConfirmDelete}
                            disabled={deleteTemplateMutation.isPending}
                        >
                            {deleteTemplateMutation.isPending ? 'Deleting…' : 'Delete'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};
